import React from "react";

interface GrafanaCloudInstructionsProps {
  productName?: string;
  hostingType?: "cloud" | "self_hosted";
}

const GrafanaCloudInstructions: React.FC<GrafanaCloudInstructionsProps> = ({
  productName = "TierZero AI",
  hostingType = "cloud",
}) => {
  if (hostingType === "self_hosted") {
    return (
      <div className="text-muted-foreground space-y-3">
        <div className="mb-4">
          <p className="mb-2 font-bold">GCP IAP Authentication Setup:</p>
          <ol className="text-sm space-y-2 list-decimal list-inside">
            <li>
              Identify or create a GCP service account that {productName} will impersonate to access
              your Grafana instance behind IAP.
            </li>
            <li>
              Grant {productName}&apos;s service account the{" "}
              <span className="text-amber-600">roles/iam.serviceAccountTokenCreator</span> role on
              your service account. This allows {productName} to mint short-lived tokens.
            </li>
            <li>
              In the{" "}
              <span className="text-amber-600">
                GCP Console &gt; Security &gt; Identity-Aware Proxy
              </span>
              , add your service account as an <span className="text-amber-600">IAP-secured Web App User</span>{" "}
              on the Grafana resource.
            </li>
            <li>
              Find the <span className="text-amber-600">IAP OAuth Client ID</span> for your Grafana
              resource. Go to{" "}
              <span className="text-amber-600">
                GCP Console &gt; APIs &amp; Services &gt; Credentials
              </span>{" "}
              and locate the OAuth 2.0 Client ID associated with your IAP-protected resource
              (ends in <span className="text-amber-600">.apps.googleusercontent.com</span>).
            </li>
            <li>
              Enter the <span className="text-amber-600">GCP Service Account Email</span> and{" "}
              <span className="text-amber-600">IAP OAuth Client ID</span> in the fields below.
            </li>
            <li>
              Enter your Grafana endpoint URL (e.g.,{" "}
              <span className="text-amber-600">https://grafana.example.com</span>).
            </li>
          </ol>
        </div>

        <div className="mb-4">
          <p className="mb-2 font-bold">Datasource UID Instructions:</p>
          <ol className="text-sm space-y-2 list-decimal list-inside">
            <li>
              In your Grafana instance, click on{" "}
              <span className="text-amber-600">Connections</span>
              {" > "}
              <span className="text-amber-600">Data sources</span>.
            </li>
            <li>
              Click on a data source (e.g., your Loki datasource for logs).
            </li>
            <li>
              Look at the URL to find the datasource UID (the last segment of the path).
            </li>
            <li>
              Repeat for:
              <ul className="ml-6 mt-1 space-y-1">
                <li>
                  <span className="text-primary">Logs</span> - Loki datasource
                </li>
                <li>
                  <span className="text-primary">Metrics</span> - Prometheus / Mimir datasource
                </li>
                <li>
                  <span className="text-primary">Traces</span> - Tempo datasource
                </li>
              </ul>
            </li>
            <li>
              Paste the datasource UIDs into the corresponding fields below.
            </li>
          </ol>
          <p className="text-xs text-muted-foreground mt-3">
            Note: At least one datasource UID is required to test and connect the integration.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="text-muted-foreground space-y-3">
      <div className="mb-4">
        <p className="mb-2 font-bold">API Token Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            Log into your Grafana account at{" "}
            <span className="text-amber-600">https://&lt;org_slug&gt;.grafana.net</span>.
          </li>
          <li>
            Click on <span className="text-amber-600">Administration</span>
            {" > "}
            <span className="text-amber-600">Users and access</span>
            {" > "}
            <span className="text-amber-600">Service accounts</span>.
          </li>
          <li>
            Click <span className="text-amber-600">Add service account</span>.
          </li>
          <li>
            Give it a display name (e.g., &quot;{productName}&quot;).
          </li>
          <li>
            Assign the <span className="text-amber-600">Viewer</span> role and click{" "}
            <span className="text-amber-600">Create</span>.
          </li>
          <li>
            Under the Tokens section, click{" "}
            <span className="text-amber-600">+ Add service account token</span>.
          </li>
          <li>
            Click <span className="text-amber-600">Generate token</span> and copy the generated token.
          </li>
          <li>
            Paste the token into the <span className="text-amber-600">API Token</span> field below.
          </li>
        </ol>
      </div>

      <div className="mb-4">
        <p className="mb-2 font-bold">Datasource UID Instructions:</p>
        <ol className="text-sm space-y-2 list-decimal list-inside">
          <li>
            In your Grafana instance, click on{" "}
            <span className="text-amber-600">Connections</span>
            {" > "}
            <span className="text-amber-600">Data sources</span>.
          </li>
          <li>
            Click on a data source (e.g., &quot;grafanacloud-&lt;org_slug&gt;-logs&quot; for Loki).
          </li>
          <li>
            Look at the URL:{" "}
            <span className="text-amber-600">
              https://&lt;org_slug&gt;.grafana.net/connections/datasources/edit/grafanacloud-logs
            </span>
            <br />
            The last part of the URL is the datasource UID (e.g., &quot;grafanacloud-logs&quot;).
          </li>
          <li>
            Repeat for:
            <ul className="ml-6 mt-1 space-y-1">
              <li>
                <span className="text-primary">Logs</span> - Loki datasource (e.g., &quot;grafanacloud-logs&quot;)
              </li>
              <li>
                <span className="text-primary">Metrics</span> - Prometheus / Mimir datasource (e.g., &quot;grafanacloud-prom&quot;)
              </li>
              <li>
                <span className="text-primary">Traces</span> - Tempo datasource (e.g., &quot;grafanacloud-traces&quot;)
              </li>
            </ul>
          </li>
          <li>
            Paste the datasource UIDs into the corresponding fields below.
          </li>
        </ol>
        <p className="text-xs text-muted-foreground mt-3">
          Note: At least one datasource UID is required to test and connect the integration.
        </p>
      </div>
    </div>
  );
};

export default GrafanaCloudInstructions;
